import React, { useMemo } from "react";
import "./LoaderComponent.scss";

const FALLBACK_THEME = {
  primary: "#C9A14A",
  secondary: "#F7F1E8",
  accent: "#E6C77A",
  text: "#2B2B2B",
  surface: "#FFFFFF",
  border: "#C9A14A",
};

const normalizeColor = (value) => {
  if (typeof value !== "string") return null;

  const v = value.trim();
  if (!v) return null;

  if (
    v.startsWith("#") ||
    v.startsWith("rgb") ||
    v.startsWith("rgba") ||
    v.startsWith("hsl") ||
    v.startsWith("hsla") ||
    v.startsWith("var(")
  ) {
    return v;
  }

  if (/^[0-9A-Fa-f]{6}$/.test(v)) return `#${v}`;
  if (/^[0-9A-Fa-f]{3}$/.test(v)) return `#${v}`;

  return v;
};

const pickColor = (...values) => {
  for (const value of values) {
    const normalized = normalizeColor(value);
    if (normalized) return normalized;
  }
  return null;
};

const resolveTheme = (themeData = {}) => {
  const nested = themeData?.theme || {};

  const primary =
    pickColor(
      themeData?.primary,
      themeData?.primaryColor,
      nested?.primary,
      nested?.primaryColor
    ) || FALLBACK_THEME.primary;

  const secondary =
    pickColor(
      themeData?.secondary,
      themeData?.secondaryColor,
      nested?.secondary,
      nested?.secondaryColor
    ) || FALLBACK_THEME.secondary;

  const accent =
    pickColor(
      themeData?.accent,
      themeData?.accentColor,
      nested?.accent,
      nested?.accentColor
    ) || FALLBACK_THEME.accent;

  const text =
    pickColor(
      themeData?.text,
      themeData?.textColor,
      nested?.text,
      nested?.textColor
    ) || FALLBACK_THEME.text;

  const surface =
    pickColor(
      themeData?.surface,
      themeData?.surfaceColor,
      nested?.surface,
      nested?.surfaceColor
    ) || secondary;

  const border =
    pickColor(
      themeData?.border,
      themeData?.borderColor,
      nested?.border,
      nested?.borderColor
    ) || primary;

  return {
    primary,
    secondary,
    accent,
    text,
    surface,
    border,
  };
};

const LoaderComponent = ({ fullScreen = true, theme = null }) => {
  const loaderTheme = useMemo(() => resolveTheme(theme || {}), [theme]);

  const cssVars = useMemo(
    () => ({
      "--wl-primary": loaderTheme.primary,
      "--wl-secondary": loaderTheme.secondary,
      "--wl-accent": loaderTheme.accent,
      "--wl-text": loaderTheme.text,
      "--wl-surface": loaderTheme.surface,
      "--wl-border": loaderTheme.border,
    }),
    [loaderTheme]
  );

  return (
    <div
      className={`wedding-loader ${fullScreen ? "wedding-loader--fullscreen" : ""}`}
      style={cssVars}
    >
      <div className="wedding-loader__scene" aria-hidden="true">
        <div className="wedding-loader__heart">♥</div>

        <div className="wedding-loader__couple">
          <div className="person person--bride">
            <div className="person__head"></div>
            <div className="person__body person__body--bride"></div>
          </div>

          <div className="person person--groom">
            <div className="person__head"></div>
            <div className="person__body person__body--groom"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoaderComponent;